import React, { useMemo } from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { ReceivingItemDto } from '../../services/receivingService';

interface ReceivingCompletedProps {
  sessionNumber: string;
  items: ReceivingItemDto[];
  startedAt?: string;
  onNewSession: () => void;
  onLogout: () => void;
}

const ReceivingCompleted: React.FC<ReceivingCompletedProps> = ({
  sessionNumber, items, startedAt, onNewSession, onLogout,
}) => {
  const { t } = useTranslation('warehouse');
  const stats = useMemo(() => {
    const totalItems = items.reduce((sum, i) => sum + i.receivedQuantity, 0);
    const totalProducts = items.length;
    const placed = items.filter(i => i.status === 'PutAway');
    const unplaced = items.filter(i => i.status !== 'PutAway');

    let duration = '';
    if (startedAt) {
      const ms = Date.now() - new Date(startedAt).getTime();
      const minutes = Math.floor(ms / 60000);
      duration = minutes < 60
        ? t('screens.receiving.completed.durationMinutes', { count: minutes })
        : t('screens.receiving.completed.durationHours', { hours: Math.floor(minutes / 60), minutes: minutes % 60 });
    }

    return { totalItems, totalProducts, placed: placed.length, unplaced: unplaced.length, duration };
  }, [items, startedAt, t]);

  return (
    <Box sx={{
      p: { xs: 1.5, sm: 3 }, height: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: { xs: 2, sm: 3 },
    }}>
      <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main' }} />

      <Typography variant="h4" sx={{ fontWeight: 700 }}>
        {t('screens.receiving.completed.title')}
      </Typography>

      <Paper sx={{ p: { xs: 1.5, sm: 3 }, bgcolor: 'background.paper', borderRadius: 2, width: '100%', maxWidth: 480, minWidth: { xs: 0, sm: 400 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <StatRow label={t('screens.receiving.completed.delivery')} value={sessionNumber} />
          <StatRow label={t('screens.receiving.completed.received')} value={t('screens.receiving.completed.receivedValue', { items: stats.totalItems, products: stats.totalProducts })} />
          <StatRow label={t('screens.receiving.completed.placed')} value={`${stats.placed}/${stats.totalProducts}`} />
          {stats.unplaced > 0 && (
            <StatRow label={t('screens.receiving.completed.unplaced')} value={t('screens.receiving.completed.unplacedValue', { count: stats.unplaced })} />
          )}
          {stats.duration && <StatRow label={t('screens.receiving.completed.duration')} value={stats.duration} />}
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNewSession}
          size="large"
          sx={{ minWidth: 200, fontSize: 16, fontWeight: 700 }}
        >
          {t('screens.receiving.completed.newDelivery')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={onLogout}
          size="large"
        >
          {t('screens.receiving.completed.logout')}
        </Button>
      </Box>
    </Box>
  );
};

const StatRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body1" sx={{ color: 'text.secondary' }}>{label}:</Typography>
    <Typography variant="body1" sx={{ fontWeight: 600 }}>{value}</Typography>
  </Box>
);

export default ReceivingCompleted;
